import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { useTranslation } from "locales";
import useAuthUser from "hooks/useAuthUser";
import emitter, { EVENTS } from "lib/emitter";
import providers from "lib/providers";
import BaseAvatar from "components/BaseAvatar";
import ContentHeader from "components/ContentHeader";
import ModalWindow from "components/ModalWindow";
import "./style.scss";

interface Props {
  show: boolean;
  onClose: () => void;
  onGroupCreated: (groupId: string) => void;
  channelMode?: boolean;
}

// Backend constraint: `Field name: size must be between 1 and 50`.
const GROUP_NAME_MAX = 50;

function CreateGroupModal({ show, onClose, onGroupCreated, channelMode = false }: Props) {
  const { t } = useTranslation();
  const { user: authUser } = useAuthUser();
  const [groupName, setGroupName] = useState("");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<UserPublicData[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserPublicData[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [callsEnabled, setCallsEnabled] = useState(false);
  const [topicsEnabled, setTopicsEnabled] = useState(false);
  const [isPublic, setIsPublic] = useState(channelMode);
  const [isChannel, setIsChannel] = useState(channelMode);

  // A failed create's error banner must not survive close/reopen.
  useEffect(() => {
    if (show) setCreateError("");
  }, [show]);

  useEffect(() => {
    if (!search.trim()) {
      setUsers([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await providers.UserPublicDataProvider.search({
          data: { relevantTerm: search, size: 20 },
        });
        setUsers(response.result.filter((u: UserPublicData) => u.id !== authUser?.id));
      } catch {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [search, authUser]);

  const toggleUser = useCallback((user: UserPublicData) => {
    setSelectedUsers((prev) => {
      const exists = prev.find((u) => u.id === user.id);
      if (exists) return prev.filter((u) => u.id !== user.id);
      return [...prev, user];
    });
  }, []);

  const removeUser = useCallback((userId: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  const isSelected = useMemo(() => {
    const ids = new Set(selectedUsers.map((u) => u.id));
    return (id: string) => ids.has(id);
  }, [selectedUsers]);

  const handleCreate = useCallback(async () => {
    if (!authUser || !groupName.trim()) return;
    if (!isChannel && selectedUsers.length === 0) return;
    setCreating(true);
    setCreateError("");
    try {
      const group = await providers.GroupsProvider.create({
        data: {
          ownerId: authUser.id,
          name: groupName.trim(),
          isPublic: isChannel ? true : isPublic,
          callsEnabled: isChannel ? false : callsEnabled,
          isChannel,
        },
      });
      await providers.GroupsProvider.updateMembers({
        data: selectedUsers.map((u) => ({
          id: group.id,
          memberId: u.id,
          status: true,
        })),
      });
      if (topicsEnabled && !isChannel) {
        // Enable topics right at creation — the full stack already exists
        // (GroupInfoPanel toggles the same mutation later). Non-fatal: the
        // group works without topics, same as today.
        await providers.GroupsProvider.toggleTopics(group.id, true).catch(console.error);
      }
      onGroupCreated(group.id);
      // The group's MessageThread is created server-side asynchronously — a
      // single 500ms refresh often fired before it existed, so a fresh channel
      // (created with zero members) didn't appear until a manual reload. Retry
      // the list refresh a few times so it reliably shows up.
      [300, 1000, 2500].forEach((ms) => setTimeout(() => emitter.emit(EVENTS.THREADS_REFRESH), ms));
      onClose();
      setGroupName("");
      setSelectedUsers([]);
      setSearch("");
      setCallsEnabled(false);
      setTopicsEnabled(false);
      setIsPublic(channelMode);
      setIsChannel(channelMode);
    } catch (e: any) {
      console.error("Failed to create group:", e);
      // Surface the backend message (e.g. name length constraint) — it used
      // to die silently in the console.
      const backendMessage = (Array.isArray(e) ? e[0]?.message : undefined) || e?.message || e?.errors?.[0]?.message;
      setCreateError(
        backendMessage
          ? String(backendMessage)
          : isChannel
          ? t("Failed to create the channel. Please try again.")
          : t("Failed to create the group. Please try again.")
      );
    } finally {
      setCreating(false);
    }
  }, [
    authUser,
    groupName,
    selectedUsers,
    isPublic,
    callsEnabled,
    topicsEnabled,
    isChannel,
    channelMode,
    onGroupCreated,
    onClose,
    t,
  ]);

  const canCreate =
    groupName.trim().length > 0 &&
    groupName.trim().length <= GROUP_NAME_MAX &&
    (isChannel || selectedUsers.length > 0) &&
    !creating;

  return (
    <ModalWindow show={show} onClose={onClose}>
      <div className="create-group-modal">
        <ContentHeader>{isChannel ? t("Create Channel") : t("Create Group")}</ContentHeader>

        <div className="create-group-modal__group-name">
          <input
            type="text"
            placeholder={isChannel ? t("Channel name") : t("Group name")}
            value={groupName}
            maxLength={GROUP_NAME_MAX}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGroupName(e.target.value)}
            autoFocus
          />
          {groupName.length >= GROUP_NAME_MAX - 10 && (
            <div className="create-group-modal__name-counter">
              {groupName.length}/{GROUP_NAME_MAX}
            </div>
          )}
        </div>

        {createError && <div className="create-group-modal__error">{createError}</div>}

        {selectedUsers.length > 0 && (
          <div className="create-group-modal__selected">
            {selectedUsers.map((user) => (
              <span key={user.id} className="create-group-modal__chip">
                {user.screenName}
                <button type="button" onClick={() => removeUser(user.id)}>
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="create-group-modal__search">
          <svg className="create-group-modal__search-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
          <input
            type="text"
            placeholder={t("Search users to add...")}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          />
        </div>

        <div className="create-group-modal__user-list">
          {loading && <div className="create-group-modal__loading">{t("Searching...")}</div>}
          {!loading && search.trim() && users.length === 0 && (
            <div className="create-group-modal__empty">{t("No users found")}</div>
          )}
          {!loading &&
            users.map((user) => (
              <div
                key={user.id}
                className={"create-group-modal__user-item" + (isSelected(user.id) ? " __selected" : "")}
                onClick={() => toggleUser(user)}
              >
                <BaseAvatar user={user} size={36} />
                <span className="create-group-modal__user-name">{user.screenName}</span>
                <div className={"create-group-modal__check" + (isSelected(user.id) ? " __checked" : "")}>
                  {isSelected(user.id) && "✓"}
                </div>
              </div>
            ))}
        </div>

        {!channelMode && (
          <div className="create-group-modal__option-row">
            <label className="create-group-modal__checkbox-label">
              <input
                type="checkbox"
                checked={isChannel}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setIsChannel(e.target.checked);
                  if (e.target.checked) setIsPublic(true);
                }}
              />
              <span>{t("Channel (broadcast, only owner posts)")}</span>
            </label>
          </div>
        )}

        {!isChannel && (
          <div className="create-group-modal__option-row">
            <label className="create-group-modal__checkbox-label">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsPublic(e.target.checked)}
              />
              <span>{t("Public group")}</span>
            </label>
          </div>
        )}

        {!isChannel && (
          <div className="create-group-modal__option-row">
            <label className="create-group-modal__checkbox-label">
              <input
                type="checkbox"
                checked={callsEnabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCallsEnabled(e.target.checked)}
              />
              <span>{t("Enable video calls")}</span>
            </label>
          </div>
        )}

        {!isChannel && (
          <div className="create-group-modal__option-row">
            <label className="create-group-modal__checkbox-label">
              <input
                type="checkbox"
                checked={topicsEnabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTopicsEnabled(e.target.checked)}
              />
              <span>{t("Enable topics")}</span>
            </label>
          </div>
        )}

        <div className="create-group-modal__actions">
          <button type="button" className="create-group-modal__btn __secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="create-group-modal__btn __primary"
            onClick={handleCreate}
            disabled={!canCreate}
          >
            {creating ? "..." : isChannel ? t("Create Channel") : t("Create")}
          </button>
        </div>
      </div>
    </ModalWindow>
  );
}

export default CreateGroupModal;
